import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import DropDownStyles, {
  DropDownStylesDark,
  defaultSelectPortalProps,
} from "../staff/dropdownStyles";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector, useDispatch } from "react-redux";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import { floorLoyaltyPoints } from "../../utils/loyaltyPoints";
import useThemeColor from "../../hooks/useThemeColor";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import StatusBadge from "../../components/CustomComponents/StatusBadge/StatusBadge";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import { applyCatalogTotalFromFetch } from "../../utils/catalogTotalRecords";


import useProfileAndSettings from "../../hooks/useProfileAndSettings";
import ModernGridToolbar, {
  ModernGridToolbarFilterPill,
} from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";
import { getModernGridFilterPillSelectStyles } from "../../components/CustomComponents/ModernGridToolbar/modernGridToolbarSelectStyles";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";

const REWARD_SORT_FIELDS = {
  Reward_ID: "Reward_ID",
  RewardName: "RewardName",
  RequiredPoints: "RequiredPoints",
};

const REWARD_SORT_LABELS = {
  [REWARD_SORT_FIELDS.Reward_ID]: "ID",
  [REWARD_SORT_FIELDS.RewardName]: "Name",
  [REWARD_SORT_FIELDS.RequiredPoints]: "Points Required",
};

const RewardsManagement = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const dispatch = useDispatch();
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const filterPillSelectStyles = useMemo(
    () => getModernGridFilterPillSelectStyles(dropdownStyles, isDarkMode),
    [dropdownStyles, isDarkMode]
  );
  const themeColor = useThemeColor();
  const SHOW_BULK_ACTIONS = false;

  const { getCompCurrency } = useProfileAndSettings();
  const currencySymbols = { GBP: "£", USD: "$", EUR: "€" };
  const getCurrencySymbol = (currencyCode) => currencySymbols[currencyCode] || currencyCode;
  const compCurrency = getCompCurrency();
  const currencySymbol = getCurrencySymbol(compCurrency);

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [rewards, setRewards] = useState([]);
  const [statusConfirmReward, setStatusConfirmReward] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedRewards, setSelectedRewards] = useState(new Set());
  const rewardsFetchSeqRef = useRef(0);
  const lastSelectedIndex = useRef(null);
  const [hasLoyaltyRule, setHasLoyaltyRule] = useState(false);
  const [loyaltyRuleChecked, setLoyaltyRuleChecked] = useState(false);

  const pageKey = "rewards";
  const defaultVisibleColumns = {
    name: true,
    type: true,
    pointsRequired: true,
    status: true,
    discount: false,
    freeItem: false,
  };
  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: "Reward_ID", label: "ID" };
  const defaultStatus = { value: "", label: "All" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const status = preferences.status || defaultStatus;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;

  const statusOptions = [
    { value: "", label: "All" },
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  const statusSelectValue =
    statusOptions.find((o) => String(o.value) === String(status?.value ?? "")) ??
    defaultStatus;

  const navigate = useNavigate();

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const clearFilters = () => {
    setSearch("");
    dispatch(setFilterField({ pageKey, field: "status", value: defaultStatus }));
  };

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: REWARD_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
      const mergedColumns = mergeVisibleColumns(
        preferences.visibleColumns,
        defaultVisibleColumns
      );
      dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (!preferences.status) {
      dispatch(setFilterField({ pageKey, field: "status", value: defaultStatus }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
  }, [dispatch, pageKey]);

  const fetchLoyaltyRule = useCallback(async () => {
    if (!CompID) {
      setHasLoyaltyRule(false);
      setLoyaltyRuleChecked(true);
      return;
    }
    try {
      const response = await axiosInstance.get(APIs.GET_LOYALTY_RULE, {
        params: { compID: CompID },
      });
      const isSuccess = response.data.status || response.data.Status;
      const ruleData = response.data.data || response.data.Data;
      const ruleId = ruleData?.rule_ID || ruleData?.Rule_ID;
      setHasLoyaltyRule(Boolean(isSuccess && ruleId && ruleId > 0));
    } catch (error) {
      setHasLoyaltyRule(false);
    } finally {
      setLoyaltyRuleChecked(true);
    }
  }, [CompID]);

  useEffect(() => {
    fetchLoyaltyRule();
  }, [fetchLoyaltyRule]);

  const fetchRewardsData = useCallback(async () => {
    const fetchSeq = ++rewardsFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_REWARDS, {
        params: {
          compID: CompID,
          pageNumber,
          pageSize: parseInt(pageSize.value, 10),
          search: search?.trim() || "",
        },
      });

      if (fetchSeq !== rewardsFetchSeqRef.current) return;

      const rewardsData = response.data.data || response.data.Data || [];
      const list = Array.isArray(rewardsData) ? rewardsData : [];
      const records = response.data.totalRecords || 0;
      const pages = response.data.totalPages || Math.max(1, Math.ceil(records / Number(pageSize.value)));

      setRewards(list);
      setTotalRecords(records);
      setTotalPages(pages);

      const noSearch = !search.trim();
      const noStatus = status?.value === "" || status?.value === undefined;
      if (noSearch && noStatus) {
        applyCatalogTotalFromFetch(setCatalogTotalRecords, records);
      }
    } catch (err) {
      if (fetchSeq !== rewardsFetchSeqRef.current) return;
      setRewards([]);
      setTotalRecords(0);
      setTotalPages(0);
      showErrorToast("Failed to load rewards");
    } finally {
      if (fetchSeq === rewardsFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [search, CompID, pageNumber, pageSize, status?.value]);

  const displayedRewards = useMemo(() => {
    if (status?.value === "" || status?.value === undefined) return rewards;
    return rewards.filter((r) => r.status === status.value);
  }, [rewards, status?.value]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    status?.value,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(rewardsFetchSeqRef, [
    search,
    status?.value,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => fetchRewardsData(), [fetchRewardsData]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status?.value, pageNumber, pageSize?.value]);

  const hasFiltersApplied = () =>
    search.trim() !== "" || (status && status.value !== "");

  const showEmptyCatalogState =
    initialLoadComplete && !loading && !hasFiltersApplied() && catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const toolbarTotalCount =
    status?.value !== "" && status?.value !== undefined
      ? displayedRewards.length
      : totalRecords;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No rewards match your search "${search.trim()}". Try different keywords or clear filters.`;
    }
    if (status?.value !== "" && status?.value !== undefined) {
      return "No rewards match the selected status. Try another status or clear filters.";
    }
    return "There are no rewards to show right now.";
  };

  const renderNoDataMessage = () => {
    if (loading || displayedRewards.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No rewards found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  const handleAddRewardClick = () => {
    if (!loyaltyRuleChecked) return;
    if (!hasLoyaltyRule) {
      showErrorToast(
        "A loyalty rule must be set up before creating rewards. Please create a loyalty rule first."
      );
      navigate("/loyalty/rules");
      return;
    }
    navigate("/loyalty/createReward");
  };

  const handleEdit = (record) => {
    const id = record?.reward_ID ?? record?.rewardId ?? record?.id;
    if (id != null && id !== "") {
      navigate(`/loyalty/updateReward/${id}`);
    } else {
      showErrorToast("Cannot open reward: missing reward ID.");
    }
  };

  const handleDelete = async (rewardId) => {
    if (window.confirm("Are you sure you want to delete this reward?")) {
      try {
        const response = await axiosInstance.delete(`${APIs.DELETE_REWARD}`, {
          params: { CompID, rewardID: rewardId },
        });

        if (response.data.status) {
          showSuccessToast("Reward deleted successfully");
          fetchRewardsData();
        } else {
          showErrorToast(response.data.message || "Failed to delete reward");
        }
      } catch (error) {
        showErrorToast("Error deleting reward");
      }
    }
  };

  const handleSelectReward = (rewardId, index, event) => {
    setSelectedRewards((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          const id =
            displayedRewards[i]?.reward_ID ??
            displayedRewards[i]?.rewardId ??
            displayedRewards[i]?.Reward_ID;
          if (id) newSelected.add(id);
        }
      } else {
        if (newSelected.has(rewardId)) {
          newSelected.delete(rewardId);
        } else {
          newSelected.add(rewardId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(
        displayedRewards.map(
          (reward) => reward.reward_ID ?? reward.rewardId ?? reward.Reward_ID
        )
      );
      setSelectedRewards(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedRewards(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedRewards(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedRewards.size === 0) {
      showErrorToast("No rewards selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await window.confirm(
        `Are you sure you want to delete ${selectedRewards.size} selected reward${selectedRewards.size > 1 ? "s" : ""}?`
      );

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedRewards).map((rewardId) =>
          axiosInstance.delete(`${APIs.DELETE_REWARD}`, {
            params: { CompID, rewardID: rewardId },
          })
        )
      );

      setSelectedRewards(new Set());
      lastSelectedIndex.current = null;
      fetchRewardsData();
      showSuccessToast(
        `${selectedRewards.size} reward${selectedRewards.size > 1 ? "s" : ""} deleted successfully.`
      );
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to delete selected rewards.");
      setLoading(false);
    }
  };

  const confirmRewardStatusChange = async () => {
    if (!statusConfirmReward) return;

    const rewardId =
      statusConfirmReward.reward_ID ??
      statusConfirmReward.rewardId ??
      statusConfirmReward.Reward_ID;
    const currentStatus = statusConfirmReward.status;
    const newStatus = !currentStatus;
    const rewardName =
      statusConfirmReward.rewardName ?? statusConfirmReward.RewardName;

    setRewards((prev) =>
      prev.map((r) =>
        (r.reward_ID ?? r.rewardId ?? r.Reward_ID) === rewardId
          ? { ...r, status: newStatus }
          : r
      )
    );
    setStatusConfirmReward(null);

    try {
      await axiosInstance.post(`${APIs.UPDATE_REWARD_STATUS}`, {
        CompID,
        Reward_ID: rewardId,
        Status: newStatus,
      });
      showSuccessToast(
        `"${rewardName || "Reward"}" is now ${newStatus ? "Active" : "InActive"}.`
      );
    } catch (error) {
      setRewards((prev) =>
        prev.map((r) =>
          (r.reward_ID ?? r.rewardId ?? r.Reward_ID) === rewardId
            ? { ...r, status: currentStatus }
            : r
        )
      );
      showErrorToast("Failed to update reward status.");
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Rewards Management</li>
          </ol>
          <h1 className="page-header mb-0">Rewards Management</h1>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            className="btn px-4"
            onClick={handleAddRewardClick}
            disabled={!loyaltyRuleChecked || !hasLoyaltyRule}
            title={
              loyaltyRuleChecked && !hasLoyaltyRule
                ? "Set up a loyalty rule before creating rewards"
                : undefined
            }
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: `0 2px 8px ${themeColor}40`,
              opacity: !loyaltyRuleChecked || !hasLoyaltyRule ? 0.55 : 1,
              cursor:
                !loyaltyRuleChecked || !hasLoyaltyRule
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            <i className="fas fa-plus fa-lg me-2 ms-n2 text-white" />
            Add Reward
          </button>
        </div>
      </div>

      {loyaltyRuleChecked && !hasLoyaltyRule && (
        <div
          className="alert alert-warning d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3"
          role="alert"
        >
          <span>
            A loyalty rule is required before you can create rewards.
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-dark"
            onClick={() => navigate("/loyalty/rules")}
          >
            Set up loyalty rule
          </button>
        </div>
      )}

      <div className="mb-3 mt-2">
        {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

        {showEmptyCatalogState && hasLoyaltyRule && (
          <AddButton onClick={handleAddRewardClick} title="Add Reward" />
        )}

        {showEmptyCatalogState && loyaltyRuleChecked && !hasLoyaltyRule && (
          <GridEmptyState
            isDarkMode={isDarkMode}
            themeColor={themeColor}
            title="Loyalty rule required"
            description="Set up a loyalty earn rule before creating rewards for customers."
            onClearFilters={() => navigate("/loyalty/rules")}
            clearFiltersLabel="Set up loyalty rule"
          />
        )}

        {showGridToolbar && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Rewards"
            totalCount={toolbarTotalCount}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by name..."
            bodyFlush
            topFilters={
              <ModernGridToolbarFilterPill
                iconClass="fa fa-toggle-on"
                className="modern-grid-toolbar__filter-pill--status"
              >
                <Select
                  value={statusSelectValue}
                  onChange={(val) =>
                    dispatch(setFilterField({ pageKey, field: "status", value: val }))
                  }
                  options={statusOptions}
                  styles={filterPillSelectStyles}
                  placeholder="All Status"
                  isSearchable={false}
                  {...defaultSelectPortalProps}
                />
              </ModernGridToolbarFilterPill>
            }
            headerControls={
              <ColumnSelection
                visibleColumns={visibleColumns}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
              />
            }
          >
            <div className="px-3 pb-3">
              {SHOW_BULK_ACTIONS && selectedRewards.size > 0 && (
                <div className="mb-3 p-3 bg-light rounded d-flex align-items-center justify-content-between">
                  <span className="fw-bold text-primary">
                    {selectedRewards.size} reward{selectedRewards.size > 1 ? "s" : ""} selected
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={handleDeselectAll}
                  >
                    Clear Selection
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={handleBulkDelete}
                  >
                    Delete
                  </button>
                </div>
              )}

              <GridSectionLoader loading={loading} minHeight="280px">
                {renderNoDataMessage()}

                {!loading && displayedRewards.length > 0 && (
                  <div
                    className="table-responsive table-container mt-3"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                    }}
                  >
                    <table
                      className="table table-hover"
                      style={{ "--modern-grid-sort-active": themeColor }}
                    >
                      <thead
                        className="bg-light sticky-header"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10000,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          {SHOW_BULK_ACTIONS && (
                            <th className="pt-2 pb-2">
                              <input
                                type="checkbox"
                                onChange={handleSelectAll}
                                checked={
                                  displayedRewards.length > 0 &&
                                  selectedRewards.size === displayedRewards.length
                                }
                              />
                            </th>
                          )}
                          {visibleColumns.name && (
                            <SortableColumnHeader
                              sortField={REWARD_SORT_FIELDS.RewardName}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              Reward
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.type && <th className="pt-2 pb-2">Type</th>}
                          {visibleColumns.pointsRequired && (
                            <SortableColumnHeader
                              sortField={REWARD_SORT_FIELDS.RequiredPoints}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Points Required
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.discount && (
                            <th className="pt-2 pb-2">Discount</th>
                          )}
                          {visibleColumns.freeItem && (
                            <th className="pt-2 pb-2">Free Item</th>
                          )}
                          {visibleColumns.status && (
                            <th className="pt-2 pb-2 text-center">Status</th>
                          )}
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayedRewards.map((reward, index) => {
                          const rewardId =
                            reward.reward_ID ?? reward.rewardId ?? reward.Reward_ID;
                          return (
                            <tr
                              key={rewardId ?? index}
                              onClick={(e) => {
                                if (
                                  e.target.closest(".dropdown") ||
                                  e.target.closest(".status-badge") ||
                                  e.target.closest('input[type="checkbox"]')
                                ) {
                                  return;
                                }
                                handleEdit(reward);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {SHOW_BULK_ACTIONS && (
                                <td className="align-middle" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    checked={selectedRewards.has(rewardId)}
                                    onChange={(e) =>
                                      handleSelectReward(rewardId, index, e)
                                    }
                                  />
                                </td>
                              )}
                              {visibleColumns.name && (
                                <td className="align-middle">
                                  <div className="d-flex align-items-center">
                                    <i
                                      className="fas fa-gift me-3"
                                      style={{ color: themeColor, fontSize: "18px" }}
                                    />
                                    <div>
                                      <div className="fw-semibold">
                                        {reward.rewardName ?? reward.RewardName}
                                      </div>
                                      <div className="text-muted small">
                                        {reward.rewardType === "DISCOUNT_ON_SALE" &&
                                          reward.discountType &&
                                          reward.amount &&
                                          (reward.discountType === "FIXED_AMOUNT"
                                            ? `${currencySymbol}${reward.amount} off`
                                            : `${reward.amount}% off`)}
                                        {reward.rewardType === "FREE_ITEM" &&
                                          reward.itemName &&
                                          `Free ${reward.itemName}`}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              )}
                              {visibleColumns.type && (
                                <td className="align-middle">
                                  <span
                                    className={`badge ${
                                      reward.rewardType === "DISCOUNT_ON_SALE"
                                        ? "bg-success"
                                        : reward.rewardType === "DISCOUNT_ON_ITEMS"
                                          ? "bg-primary"
                                          : reward.rewardType === "DISCOUNT_ON_CATEGORY"
                                            ? "bg-info"
                                            : reward.rewardType === "FREE_ITEM"
                                              ? "bg-warning text-dark"
                                              : "bg-secondary"
                                    }`}
                                  >
                                    {reward.rewardType?.replace(/_/g, " ")}
                                  </span>
                                </td>
                              )}
                              {visibleColumns.pointsRequired && (
                                <td className="align-middle text-center">
                                  <span className="fw-semibold text-primary">
                                    {floorLoyaltyPoints(
                                      reward.requiredPoints ?? reward.RequiredPoints
                                    )}{" "}
                                    pts
                                  </span>
                                </td>
                              )}
                              {visibleColumns.discount && (
                                <td className="align-middle">
                                  {reward.rewardType?.includes("DISCOUNT") && reward.amount
                                    ? `${reward.amount}${
                                        reward.discountType === "PERCENTAGE" ? "%" : currencySymbol
                                      }`
                                    : "-"}
                                </td>
                              )}
                              {visibleColumns.freeItem && (
                                <td className="align-middle">
                                  {reward.rewardType === "FREE_ITEM" && reward.itemName
                                    ? reward.itemName
                                    : "-"}
                                </td>
                              )}
                              {visibleColumns.status && (
                                <td
                                  className="align-middle text-center"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <StatusBadge
                                    isActive={reward.status}
                                    onClick={() => setStatusConfirmReward(reward)}
                                  />
                                </td>
                              )}
                              <td
                                className="align-middle text-center"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <TableActionsMenu>
                                  <TableActionsItem
                                    icon="fa fa-edit"
                                    onClick={() => handleEdit(reward)}
                                  >
                                    Edit
                                  </TableActionsItem>
                                  <TableActionsItem
                                    danger
                                    icon="fa fa-trash"
                                    onClick={() => handleDelete(rewardId)}
                                  >
                                    Delete
                                  </TableActionsItem>
                                </TableActionsMenu>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={status?.value !== "" && status?.value !== undefined ? displayedRewards.length : totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>

      <CustomModal
        title="Change reward status?"
        subtitle={
          statusConfirmReward
            ? `Are you sure you want to set "${
                statusConfirmReward.rewardName ?? statusConfirmReward.RewardName ?? "this reward"
              }" to ${statusConfirmReward.status ? "InActive" : "Active"}?`
            : ""
        }
        fontSize="18px"
        fontWeight={400}
        onClose={() => setStatusConfirmReward(null)}
        isOpen={!!statusConfirmReward}
        isCentered={true}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        submitButtonText="Confirm"
        onSubmit={confirmRewardStatusChange}
        dialogMaxWidth="400px"
        minHeight="auto"
        maxHeight="320px"
      />
    </div>
  );
};

export default RewardsManagement;
